import React, { useEffect, useState, useRef } from 'react';
import { X, Loader2, AlertCircle, Printer } from 'lucide-react';
import axios from '@/lib/axios';
import { API_BASE_URL } from '@/config';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface KFSData {
    borrower_name: string;
    lender_details: {
        name: string;
        logo: string | null;
    };
    loan_proposal_no: string;
    loan_type?: string;
    kfs_validity_days?: string;
    sanctioned_amount: number;
    disbursal_schedule: string;
    loan_term: string;
    instalment_type: string;
    number_of_epis: number;
    epi_amount: number;
    repayment_commencement: string;
    broken_period_interest?: string;
    Interest_rate_to_show: string;
    proc_fee: number;
    gst_on_proc_fee: number;
    proc_fee_amt_wgst_re: number;
    proc_fee_amt_wgst_re_third_party: number;
    insurance_fee_amt_re: number;
    insurance_fee_amt_re_third_party: number;
    valuation_fees_re?: number;
    valuation_fees_third_party?: number;
    payment_bounce_charges?: string;
    apr: string;
    penal_charge: string;
    other_penal_charge: string;
    prepayment_charge: string;
    fc_charge: string;
    cool_off_period: string;
    future_tran_to_other_re: string;
    total_interest_to_pay: number;
    total_fee_re: number;
    total_fee_third_party: number;
    repay_type?: string;
    clause_rec?: string;
    clause_gr?: string;
    gr_officer_name?: string;
    gr_officer_designation?: string;
    gr_officer_phone?: string;
    gr_officer_email?: string;
    gr_officer_address?: string;
    grievance_policy_url?: string;
    recovery_agent_document_url?: string;
    recovery_agent_name?: string;
    kfs_primary_color?: string;
    kfs_hover_color?: string;
    disbursement_schedule_terms?: string;
    first_payment_due_date?: string;
    non_equated_periodic_instalments?: string;
    repayment_schedule: Array<{
        EDI_Day: number;
        EDI_Repayments: number;
        POS_After_Payment?: number;
        Principal_Component?: number;
        Interest_Component?: number;
    }>;
}

interface KFSModalProps {
    isOpen: boolean;
    onClose: () => void;
    applicationId?: string; // Optional - if not provided, uses sample data
}

const KFSModal: React.FC<KFSModalProps> = ({ isOpen, onClose, applicationId }) => {
    const [data, setData] = useState<KFSData | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            fetchKFSData();
        }
    }, [isOpen]);

    const handleDownloadPDF = async () => {
        if (!contentRef.current || !data) return;
        
        setIsGeneratingPDF(true);
        try {
            // Get the content element
            const element = contentRef.current;
            
            // Store original styles
            const originalWidth = element.style.width;
            const originalMaxWidth = element.style.maxWidth;
            const originalPosition = element.style.position;
            
            // Set fixed width for consistent rendering (A4 width equivalent in pixels)
            element.style.width = '1200px';
            element.style.maxWidth = '1200px';
            element.style.position = 'absolute';
            element.style.left = '-9999px';
            
            // Wait for layout to settle
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Create canvas from the element with fixed width
            const canvas = await html2canvas(element, {
                scale: 1.2, // Reduced for smaller file size
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                width: 1200,
                windowWidth: 1200
            });
            
            // Restore original styles
            element.style.width = originalWidth;
            element.style.maxWidth = originalMaxWidth;
            element.style.position = originalPosition;
            element.style.left = '';
            
            // Calculate PDF dimensions
            const imgWidth = 210; // A4 width in mm
            const pageHeight = 297; // A4 height in mm
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            
            // Create PDF
            const pdf = new jsPDF('p', 'mm', 'a4');
            let position = 0;
            
            // Convert to JPEG with compression for smaller file size
            const imgData = canvas.toDataURL('image/jpeg', 0.6); // JPEG with 60% quality for smaller size
            pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
            heightLeft -= pageHeight;
            
            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
                heightLeft -= pageHeight;
            }
            
            // Download the PDF
            pdf.save(`KFS_${data.loan_proposal_no}_${data.borrower_name}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Failed to generate PDF. Please try again.');
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const fetchKFSData = async () => {
        setIsLoading(true);
        setError(null);
        try {
            // Use different API based on whether applicationId is provided
            const apiUrl = applicationId
                ? `${API_BASE_URL}/api/caprion/get_latest_offer/`
                : `${API_BASE_URL}/api/get_sample_kfs_details/`;
            
            const response = await axios.get(apiUrl, { withCredentials: true });
            
            // Transform the sample KFS data to match the expected KFSData interface
            const kfsData = response.data.data;
            const borrowerName = response.data.borrower_name;
            
            // Use loan proposal number from API, or generate random if not available
            const loanProposalNo = kfsData.loan_proposal_no || kfsData.cname || ('INTL-' + Math.floor(100000 + Math.random() * 900000));
            
            const transformedData: KFSData = {
                borrower_name: borrowerName,
                lender_details: {
                    name: kfsData.lender || 'CAPRION',
                    logo: null
                },
                loan_proposal_no: loanProposalNo,
                loan_type: kfsData.loan_type,
                kfs_validity_days: kfsData.kfs_validity_days,
                sanctioned_amount: kfsData.offer_stp || 0,
                disbursal_schedule: kfsData.disbursal_schedule,
                loan_term: kfsData.loan_tenure_to_show,
                instalment_type: kfsData.repay_type,
                number_of_epis: kfsData.loan_tenure,
                epi_amount: kfsData.daily_repayment || 0,
                repayment_commencement: kfsData.first_emi_Date,
                broken_period_interest: kfsData.broken_period_interest,
                Interest_rate_to_show: kfsData.roi_to_show,
                proc_fee: kfsData.proc_fee || 0,
                gst_on_proc_fee: kfsData.gst_on_proc_fee || 0,
                proc_fee_amt_wgst_re: (kfsData.proc_fee || 0) + (kfsData.gst_on_proc_fee || 0),
                proc_fee_amt_wgst_re_third_party: kfsData.proc_fee,
                insurance_fee_amt_re: kfsData.insurance_fee_amt_re,
                insurance_fee_amt_re_third_party: kfsData.insurance_fee_amt_re_third_party,
                payment_bounce_charges: kfsData.payment_bounce_charges,
                apr: kfsData.apr ? kfsData.apr + '%' : undefined,
                penal_charge: kfsData.penal_charge,
                other_penal_charge: kfsData.other_penal_charge,
                prepayment_charge: kfsData.prepayment_charge,
                fc_charge: kfsData.fc_charge,
                cool_off_period: kfsData.cool_off_period,
                future_tran_to_other_re: kfsData.future_tran_to_other_re,
                total_interest_to_pay: kfsData.offer_stp_winterest && kfsData._offer_data_detail_?._loan_amt_
                    ? kfsData.offer_stp_winterest - kfsData._offer_data_detail_._loan_amt_
                    : 0,
                total_fee_re: kfsData.proc_fee || 0,
                total_fee_third_party: kfsData.gst_on_proc_fee || 0,
                repay_type: kfsData.repay_type,
                clause_rec: kfsData.clause_rec,
                clause_gr: kfsData.clause_gr,
                gr_officer_name: kfsData.gr_officer_name,
                gr_officer_designation: kfsData.gr_officer_designation,
                gr_officer_phone: kfsData.gr_officer_phone,
                gr_officer_email: kfsData.gr_officer_email,
                gr_officer_address: kfsData.gr_officer_address,
                grievance_policy_url: kfsData.grievance_policy_url,
                recovery_agent_document_url: kfsData.recovery_agent_document_url,
                recovery_agent_name: kfsData.recovery_agent_name,
                kfs_primary_color: kfsData.kfs_primary_color,
                kfs_hover_color: kfsData.kfs_hover_color,
                first_payment_due_date: kfsData.first_emi_Date,
                disbursement_schedule_terms: kfsData.disbursement_schedule_terms,
                non_equated_periodic_instalments: kfsData.non_equated_periodic_instalments,
                repayment_schedule: (kfsData._rps_ || []).map((rps: any) => ({
                    EDI_Day: rps._edi_day_,
                    EDI_Repayments: rps._edi_repayments_,
                    POS_After_Payment: rps._pos_after_payment_,
                    Principal_Component: rps._principal_component_,
                    Interest_Component: rps._interest_component_
                }))
            };
            
            setData(transformedData);
        } catch (err: any) {
            console.error('Failed to fetch KFS details', err);
            setError(err.response?.data?.error || 'Failed to load Key Fact Statement details.');
        } finally {
            setIsLoading(false);
        }
    };

    const formatCurrency = (amount: number | string) => {
        if (typeof amount === 'string') return amount;
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    if (!isOpen) return null;

    const primaryColor = data?.kfs_primary_color || '#003366';
    const hoverColor = data?.kfs_hover_color || '#002244';

    return (
        <div className="fixed inset-0 z-50 bg-gray-50 overflow-hidden">
            <style dangerouslySetInnerHTML={{ __html: `
                .kfs-table-container::-webkit-scrollbar {
                    height: 8px;
                    display: block !important;
                }
                .kfs-table-container::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 4px;
                }
                .kfs-table-container::-webkit-scrollbar-thumb {
                    background: ${primaryColor};
                    border-radius: 4px;
                }
                .kfs-table-container::-webkit-scrollbar-thumb:hover {
                    background: ${hoverColor};
                }
                .kfs-table-container {
                    scrollbar-width: auto;
                    scrollbar-color: ${primaryColor} #f1f1f1;
                }
            ` }} />
            
            {/* Header */}
            <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
                <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900">Key Fact Statement</h2>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleDownloadPDF}
                            disabled={isGeneratingPDF || !data}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isGeneratingPDF ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Generating PDF...
                                </>
                            ) : (
                                <>
                                    <Printer className="h-4 w-4" />
                                    Download PDF
                                </>
                            )}
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            aria-label="Close"
                        >
                            <X className="h-5 w-5 text-gray-600" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="h-[calc(100vh-4rem)] overflow-y-auto">
                <div className="max-w-5xl mx-auto px-4 py-8">
                    {isLoading && (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="h-12 w-12 text-intellend-green animate-spin mb-4" />
                            <p className="text-gray-600">Loading Key Fact Statement...</p>
                        </div>
                    )}

                    {error && (
                        <div className="flex flex-col items-center justify-center py-12 px-6">
                            <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
                            <div className="text-center max-w-md">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">KFS Not Available Yet</h3>
                                <p className="text-gray-600 mb-4">
                                    Your Key Fact Statement will be available after you select a lender and proceed with the loan application.
                                </p>
                            </div>
                        </div>
                    )}

                    {data && !isLoading && !error && (
                        <div ref={contentRef} className="bg-white md:rounded-xl shadow-sm md:border overflow-hidden mb-8">
                            <div className="p-6 border-b text-white flex justify-between items-center" style={{ backgroundColor: primaryColor }}>
                                <div>
                                    <h1 className="text-2xl font-bold">KEY FACTS STATEMENT</h1>
                                    <p className="text-gray-200 text-sm mt-1">Part 1 (Interest rate and fees)</p>
                                </div>
                                {data.lender_details.logo && (
                                    <img src={data.lender_details.logo} alt={data.lender_details.name} className="h-10 object-contain" />
                                )}
                            </div>

                            <div className="p-6">
                                <div className="overflow-x-auto kfs-table-container border border-gray-300 rounded-lg mb-6">
                                    <table className="w-full border-collapse text-sm min-w-[800px]">
                                    <tbody>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">1</td>
                                            <td className="w-1/3 p-3 border-r font-bold bg-gray-50">Loan proposal/ account No.</td>
                                            <td className="p-3">{data.loan_proposal_no}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">1A</td>
                                            <td className="w-1/3 p-3 border-r font-bold bg-gray-50">Type of Loan</td>
                                            <td className="p-3">{data.loan_type || 'Term Loan'}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">1B</td>
                                            <td className="w-1/3 p-3 border-r font-bold bg-gray-50">Validity of KFS</td>
                                            <td className="p-3">{data.kfs_validity_days || '1'} Day</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">2</td>
                                            <td className="w-1/3 p-3 border-r font-bold bg-gray-50">Sanctioned Loan amount (in Rupees)</td>
                                            <td className="p-3">Rs. {(data.sanctioned_amount || 0).toFixed(1)}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">3</td>
                                            <td className="w-1/3 p-3 border-r bg-gray-50">
                                                <span className="font-bold">Disbursal schedule</span><br />
                                                <span className="text-xs text-gray-600">
                                                    (i) Disbursement in stages or 100% upfront.<br />
                                                    (ii) If it is stage wise, mention the clause of loan agreement having relevant details
                                                </span>
                                            </td>
                                            <td className="p-3">{data.disbursal_schedule}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">4</td>
                                            <td className="w-1/3 p-3 border-r font-bold bg-gray-50">Loan term (year/months/days)</td>
                                            <td className="p-3">{data.loan_term}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">5</td>
                                            <td className="w-1/3 p-3 border-r font-bold bg-gray-50">Instalment details</td>
                                            <td className="p-0">
                                                <table className="w-full border-collapse">
                                                    <thead>
                                                        <tr className="bg-gray-100 border-b">
                                                            <th className="p-2 border-r text-xs font-bold text-center">Type of instalments</th>
                                                            <th className="p-2 border-r text-xs font-bold text-center">Number of EPIs</th>
                                                            <th className="p-2 border-r text-xs font-bold text-center">EPI (₹)</th>
                                                            <th className="p-2 text-xs font-bold text-center">Commencement of repayment</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td className="p-2 border-r text-center">{data.instalment_type}</td>
                                                            <td className="p-2 border-r text-center">{data.number_of_epis}</td>
                                                            <td className="p-2 border-r text-center">Rs. {(data.epi_amount || 0).toFixed(2)}</td>
                                                            <td className="p-2 text-center">{data.repayment_commencement}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">5A</td>
                                            <td className="w-1/3 p-3 border-r font-bold bg-gray-50">Broken Period Interest / Pre-EMI Interest</td>
                                            <td className="p-3">{data.broken_period_interest || 'NA'}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">6</td>
                                            <td className="w-1/3 p-3 border-r font-bold bg-gray-50">Interest rate (%) and type (fixed or floating or hybrid)</td>
                                            <td className="p-3">{data.Interest_rate_to_show}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">7</td>
                                            <td className="p-3 font-bold bg-gray-50" colSpan={2}>Additional Information in case of Floating rate of interest</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="p-0" colSpan={3}>
                                                <div className="overflow-x-auto kfs-table-container">
                                                    <table className="w-full border-collapse">
                                                        <thead>
                                                            <tr className="bg-gray-100 border-b">
                                                                <th className="p-2 border-r text-xs font-bold text-center" rowSpan={2}>Reference Benchmark</th>
                                                                <th className="p-2 border-r text-xs font-bold text-center" rowSpan={2}>Benchmark rate (%) (B)</th>
                                                                <th className="p-2 border-r text-xs font-bold text-center" rowSpan={2}>Spread (%) (S)</th>
                                                                <th className="p-2 border-r text-xs font-bold text-center" rowSpan={2}>Final rate (%) R = (B) + (S)</th>
                                                                <th className="p-2 border-r text-xs font-bold text-center" rowSpan={2}>Reset periodicity (Months)</th>
                                                                <th className="p-2 border-b text-xs font-bold text-center" colSpan={2}>Impact of change in the reference benchmark</th>
                                                            </tr>
                                                            <tr className="bg-gray-100 border-b">
                                                                <th className="p-2 border-r text-xs font-bold text-center">EPI (₹)</th>
                                                                <th className="p-2 text-xs font-bold text-center">No. of EPIs</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                <td className="p-2 border-r text-center"></td>
                                                                <td className="p-2 border-r text-center"></td>
                                                                <td className="p-2 border-r text-center"></td>
                                                                <td className="p-2 border-r text-center"></td>
                                                                <td className="p-2 border-r text-center"></td>
                                                                <td className="p-2 border-r text-center"></td>
                                                                <td className="p-2 text-center"></td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">8</td>
                                            <td className="p-3 font-bold bg-gray-50" colSpan={2}>Fee/ Charges</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="p-0" colSpan={3}>
                                                <div className="overflow-x-auto">
                                                    <table className="border-collapse" style={{ minWidth: '900px', width: '100%' }}>
                                                        <thead>
                                                            <tr className="bg-gray-100 border-b text-center font-bold text-[10px]">
                                                                <th className="p-2 border-r" style={{ minWidth: '50px' }} colSpan={2}></th>
                                                                <th className="p-2 border-r" style={{ minWidth: '300px' }} colSpan={2}>8A Payable to RE</th>
                                                                <th className="p-2" style={{ minWidth: '300px' }} colSpan={2}>8B Payable to Third Party</th>
                                                            </tr>
                                                            <tr className="bg-gray-100 border-b">
                                                                <th className="p-2 border-r" style={{ minWidth: '50px' }} colSpan={2}></th>
                                                                <th className="p-2 border-r text-xs font-bold text-center" style={{ minWidth: '150px' }}>One-time/ Recurring</th>
                                                                <th className="p-2 border-r text-xs font-bold text-center" style={{ minWidth: '150px' }}>Amount (in ₹) or Percentage (%)</th>
                                                                <th className="p-2 border-r text-xs font-bold text-center" style={{ minWidth: '150px' }}>One-time/ Recurring</th>
                                                                <th className="p-2 text-xs font-bold text-center" style={{ minWidth: '150px' }}>Amount (in ₹) or Percentage (%)</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr className="border-b">
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '40px' }}>(i)</td>
                                                                <td className="p-2 border-r font-bold" style={{ minWidth: '180px' }}>Processing fees</td>
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '150px' }}>One-Time</td>
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '150px' }}>Rs. {data.proc_fee}</td>
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '150px' }}>One-Time</td>
                                                                <td className="p-2 text-center" style={{ minWidth: '150px' }}>Rs. {data.gst_on_proc_fee}</td>
                                                            </tr>
                                                            <tr className="border-b">
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '40px' }}>(ii)</td>
                                                                <td className="p-2 border-r font-bold" style={{ minWidth: '180px' }}>Insurance charges</td>
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '150px' }}>One-Time</td>
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '150px' }}>Rs. {data.insurance_fee_amt_re}</td>
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '150px' }}>One-Time</td>
                                                                <td className="p-2 text-center" style={{ minWidth: '150px' }}>Rs. {data.insurance_fee_amt_re_third_party}</td>
                                                            </tr>
                                                            <tr className="border-b">
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '40px' }}>(iii)</td>
                                                                <td className="p-2 border-r font-bold" style={{ minWidth: '180px' }}>Valuation fees</td>
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '150px' }}>One-Time</td>
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '150px' }}>Rs. {data.valuation_fees_re || 0}</td>
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '150px' }}>One-Time</td>
                                                                <td className="p-2 text-center" style={{ minWidth: '150px' }}>Rs. {data.valuation_fees_third_party || 0}</td>
                                                            </tr>
                                                            <tr className="border-b">
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '40px' }}>(iv)</td>
                                                                <td className="p-2 border-r font-bold" style={{ minWidth: '180px' }}>Payment Bounce Charges</td>
                                                                <td className="p-2 border-r text-center" colSpan={4}>{data.payment_bounce_charges || 'NA'}</td>
                                                            </tr>
                                                            <tr className="border-b">
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '40px' }}>(v)</td>
                                                                <td className="p-2 border-r font-bold" style={{ minWidth: '180px' }}>Any other (please specify)</td>
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '150px' }}></td>
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '150px' }}></td>
                                                                <td className="p-2 border-r text-center" style={{ minWidth: '150px' }}></td>
                                                                <td className="p-2 text-center" style={{ minWidth: '150px' }}></td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">9</td>
                                            <td className="w-1/3 p-3 border-r font-bold bg-gray-50">Annual Percentage Rate (APR) (%)</td>
                                            <td className="p-3">{data.apr}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">10</td>
                                            <td className="p-3 font-bold bg-gray-50" colSpan={2}>Details of Contingent Charges (in ₹ or %, as applicable)</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">(i)</td>
                                            <td className="w-1/3 p-3 border-r font-bold">Penal charges, if any, in case of delayed payment</td>
                                            <td className="p-3">{data.penal_charge}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">(ii)</td>
                                            <td className="w-1/3 p-3 border-r font-bold">Other penal charges, if any</td>
                                            <td className="p-3">{data.other_penal_charge}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">(iii)</td>
                                            <td className="w-1/3 p-3 border-r font-bold">Pre-payment Charges (if any)</td>
                                            <td className="p-3">{data.prepayment_charge}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">(iv)</td>
                                            <td className="w-1/3 p-3 border-r font-bold">Payment Bounce charges (in INR) (if any)</td>
                                            <td className="p-3">{data.payment_bounce_charges}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">(v)</td>
                                            <td className="w-1/3 p-3 border-r font-bold">Foreclosure charges, if applicable</td>
                                            <td className="p-3">{data.fc_charge}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">(vi)</td>
                                            <td className="w-1/3 p-3 border-r font-bold">Charges for switching of loans from floating to fixed rate and vice versa</td>
                                            <td className="p-3">0</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">(vii)</td>
                                            <td className="w-1/3 p-3 border-r font-bold">Any other charges (please specify)</td>
                                            <td className="p-3">0</td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div className="text-white p-2 font-bold text-center mb-4" style={{ backgroundColor: primaryColor }}>
                                    Part 2 (Other qualitative information)
                                </div>

                                <table className="w-full border-collapse border border-gray-300 text-sm mb-6">
                                    <tbody>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">1</td>
                                            <td className="w-1/3 p-3 border-r bg-gray-50">Clause of Loan agreement relating to engagement of recovery agents</td>
                                            <td className="p-3">{data.clause_rec || '---'}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">2</td>
                                            <td className="w-1/3 p-3 border-r bg-gray-50">Clause of Loan agreement which details grievance redressal mechanism</td>
                                            <td className="p-3">{data.clause_gr || '---'}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">3</td>
                                            <td className="w-1/3 p-3 border-r bg-gray-50">Phone number and email id of the nodal grievance redressal officer</td>
                                            <td className="p-3">
                                                <div className="text-xs space-y-1">
                                                    <p><span className="font-bold">Name:</span> {data.gr_officer_name || 'TBD'}</p>
                                                    <p><span className="font-bold">Designation:</span> {data.gr_officer_designation || 'TBD'}</p>
                                                    <p><span className="font-bold">Email:</span> {data.gr_officer_email || 'TBD'}</p>
                                                    <p><span className="font-bold">Contact:</span> {data.gr_officer_phone || 'TBD'}</p>
                                                    <p><span className="font-bold">Address:</span> {data.gr_officer_address || 'TBD'}</p>
                                                    <p><span className="font-bold">Grievances Redressal Policy:</span> {data.grievance_policy_url ? (
                                                        <a href={data.grievance_policy_url.startsWith('http') ? data.grievance_policy_url : `https://${data.grievance_policy_url}`} className="text-blue-600" target="_blank" rel="noopener noreferrer">{data.grievance_policy_url}</a>
                                                    ) : 'TBD'}</p>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">4</td>
                                            <td className="w-1/3 p-3 border-r bg-gray-50">Whether the loan is, or in future maybe, subject to transfer to other REs or securitisation (Yes / No)</td>
                                            <td className="p-3">{data.future_tran_to_other_re}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">5</td>
                                            <td className="bg-gray-50 p-3" colSpan={2}>
                                                <div className="text-xs mb-2">In case of lending under collaborative lending arrangements (e.g., co-lending/ outsourcing), following additional details may be furnished:</div>
                                                <table className="w-full border-collapse border border-gray-200 mt-2">
                                                    <thead>
                                                        <tr className="bg-gray-100 border-b">
                                                            <th className="p-2 border-r text-center">Name of the originating RE, along with its funding proportion</th>
                                                            <th className="p-2 border-r text-center">Name of the partner RE along with its proportion of funding</th>
                                                            <th className="p-2 text-center">Blended rate of interest</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td className="p-2 border-r text-center">NA</td>
                                                            <td className="p-2 border-r text-center">NA</td>
                                                            <td className="p-2 text-center">NA</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">6</td>
                                            <td className="bg-gray-50 p-3" colSpan={2}>
                                                <div className="text-xs mb-2">In case of digital loans, following specific disclosures may be furnished:</div>
                                                <table className="w-full border-collapse border border-gray-200 mt-2">
                                                    <tbody>
                                                        <tr className="border-b">
                                                            <td className="p-2 border-r">(i) Cooling off/look-up period, in terms of RE's board approved policy, during which borrower shall not be charged any penalty on prepayment of loan</td>
                                                            <td className="p-2 text-center">{data.cool_off_period}</td>
                                                        </tr>
                                                        <tr className="border-b">
                                                            <td className="p-2 border-r">(ii) Details of LSP acting as recovery agent and authorized to approach the borrower</td>
                                                            <td className="p-2 text-center">
                                                                {data.recovery_agent_document_url && (
                                                                    <>
                                                                        <a href={data.recovery_agent_document_url} className="text-blue-600 break-all text-[10px]" target="_blank" rel="noopener noreferrer">
                                                                            {data.recovery_agent_document_url}
                                                                        </a>
                                                                        <br />
                                                                    </>
                                                                )}
                                                                {data.recovery_agent_name || 'TBD'}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                </div>

                                {/* Annexure B */}
                                <div className="mt-8">
                                    <h2 className="text-white font-bold p-2 text-center mb-0 uppercase tracking-wide text-sm" style={{ backgroundColor: primaryColor }}>
                                        Annexure B - Computation of APR for Retail and MSME loans
                                    </h2>
                                    <div className="border border-gray-300 overflow-x-auto kfs-table-container">
                                        <table className="w-full border-collapse text-xs">
                                            <thead>
                                                <tr className="bg-gray-100 border-b text-center font-bold">
                                                    <th className="w-10 p-2 border-r">S. I.</th>
                                                    <th className="p-2 border-r text-left min-w-[180px]">Parameter</th>
                                                    <th className="p-2 text-left min-w-[140px]">Details</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">1</td>
                                                    <td className="p-2 border-r font-medium">Sanctioned Loan amount (in Rupees) (Sl no. 2 of the KFS– Part 1)</td>
                                                    <td className="p-2">Rs. {(data.sanctioned_amount || 0).toFixed(2)}</td>
                                                </tr>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">2</td>
                                                    <td className="p-2 border-r font-medium">Loan Term (in years/ months/ days) (Sl No.4 of the KFS– Part 1)</td>
                                                    <td className="p-2">{data.loan_term}</td>
                                                </tr>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">a)</td>
                                                    <td className="p-2 border-r">No. of instalments for payment of principal, in case of non- equated periodic loans</td>
                                                    <td className="p-2">{data.non_equated_periodic_instalments || '0'}</td>
                                                </tr>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">b)</td>
                                                    <td className="p-2 border-r">
                                                        <span className="font-bold">Type of EPI</span><br />
                                                        Amount of each EPI (in Rupees) and nos. of EPIs (e.g., no. of EMIs in case of monthly instalments) (Sl No. 5 of the KFS– Part 1)
                                                    </td>
                                                    <td className="p-2">
                                                        Type of EPI – {data.repay_type || 'Daily'}<br />
                                                        EPI – Rs. {(data.epi_amount || 0).toFixed(2)}<br />
                                                        Number of EPIs - {data.number_of_epis}
                                                    </td>
                                                </tr>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">c)</td>
                                                    <td className="p-2 border-r">No. of instalments for payment of capitalised interest, if any</td>
                                                    <td className="p-2">NA</td>
                                                </tr>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">d)</td>
                                                    <td className="p-2 border-r">Commencement of repayments, post sanction (Sl No. 5 of the KFS– Part 1)</td>
                                                    <td className="p-2">{data.repayment_commencement}</td>
                                                </tr>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">3</td>
                                                    <td className="p-2 border-r font-medium">Interest rate type (fixed or floating or hybrid) (Sl No. 6 of the KFS– Part 1)</td>
                                                    <td className="p-2">Fixed</td>
                                                </tr>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">4</td>
                                                    <td className="p-2 border-r font-medium">Rate of Interest (Sl No. 6 of the KFS– Part 1)</td>
                                                    <td className="p-2">{data.Interest_rate_to_show}</td>
                                                </tr>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">5</td>
                                                    <td className="p-2 border-r font-medium">Total Interest Amount to be charged during the entire tenor of the loan as per the rate prevailing on the date of loan agreement (Sl No. a of the KFS–Part 1)</td>
                                                    <td className="p-2">Rs. {(data.total_interest_to_pay || 0).toFixed(2)}</td>
                                                </tr>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">6</td>
                                                    <td className="p-2 border-r font-medium">Fee/ Charges payable (in Rupees)</td>
                                                    <td className="p-2 font-bold">Rs. {((data.total_fee_re || 0) + (data.total_fee_third_party || 0)).toFixed(2)}</td>
                                                </tr>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">A</td>
                                                    <td className="p-2 border-r font-medium">Payable to the RE (Sl No.8A of the KFS -Part 1)</td>
                                                    <td className="p-2">Rs. {(data.total_fee_re || 0).toFixed(2)}</td>
                                                </tr>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">B</td>
                                                    <td className="p-2 border-r font-medium">Payable to third-party routed through RE (Sl No.8B of the KFS- Part 1)</td>
                                                    <td className="p-2">Rs. {(data.total_fee_third_party || 0).toFixed(2)}</td>
                                                </tr>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">7</td>
                                                    <td className="p-2 border-r font-medium">Net disbursed amount (1-6) (in Rupees)</td>
                                                    <td className="p-2">Rs. {((data.sanctioned_amount || 0) - ((data.total_fee_re || 0) + (data.total_fee_third_party || 0))).toFixed(2)}</td>
                                                </tr>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">8</td>
                                                    <td className="p-2 border-r font-medium">Total amount to be paid by the borrower (sum of 1 and 5) (in Rupees)</td>
                                                    <td className="p-2">Rs. {((data.sanctioned_amount || 0) + (data.total_interest_to_pay || 0)).toFixed(2)}</td>
                                                </tr>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">9</td>
                                                    <td className="p-2 border-r font-medium">Annual Percentage rate- Effective annualized interest rate (in percentage) (Sl No.9 of the KFS -Part 1)</td>
                                                    <td className="p-2">{data.apr}</td>
                                                </tr>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">10</td>
                                                    <td className="p-2 border-r font-medium">Schedule of Disbursement as per Terms & Conditions</td>
                                                    <td className="p-2">{data.disbursement_schedule_terms || '100% Upfront'}</td>
                                                </tr>
                                                <tr className="border-b">
                                                    <td className="p-2 border-r text-center">11</td>
                                                    <td className="p-2 border-r font-medium">Due Date of Payment of Instalment and Interest</td>
                                                    <td className="p-2">{data.first_payment_due_date || data.repayment_commencement || 'NA'}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* REPAYMENT SCHEDULE */}
                                <div className="mt-8">
                                    <h2 className="text-white font-bold p-2 text-center mb-0 uppercase tracking-wide text-sm" style={{ backgroundColor: primaryColor }}>
                                        Annexure C - Repayment Schedule
                                    </h2>
                                    <div className="border border-gray-300 overflow-x-auto kfs-table-container">
                                        <table className="w-full text-xs border-collapse">
                                            <thead className="bg-gray-100 border-b">
                                                <tr>
                                                    <th className="p-2 border-r font-bold text-center">Instalment No.</th>
                                                    <th className="p-2 border-r font-bold text-center min-w-[100px]">Outstanding Principal (₹)</th>
                                                    <th className="p-2 border-r font-bold text-center min-w-[80px]">Principal (₹)</th>
                                                    <th className="p-2 border-r font-bold text-center min-w-[80px]">Interest (₹)</th>
                                                    <th className="p-2 font-bold text-center min-w-[80px]">Instalment (₹)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.repayment_schedule.map((row) => (
                                                    <tr key={row.EDI_Day} className="border-b hover:bg-gray-50 transition-colors">
                                                        <td className="p-2 border-r text-center">{row.EDI_Day}</td>
                                                        <td className="p-2 border-r text-right">{(row.POS_After_Payment || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                        <td className="p-2 border-r text-right">{(row.Principal_Component || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                        <td className="p-2 border-r text-right">{(row.Interest_Component || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                        <td className="p-2 text-right font-medium">{(row.EDI_Repayments || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default KFSModal;
